import { Link, NavLink, Outlet } from "react-router-dom";
import { Briefcase, ClipboardList, FileUser, LogOut, Sparkles } from "lucide-react";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { useDict } from "@/lib/i18n/context";
import { cn, initials } from "@/lib/utils";
import type { User } from "@/lib/types";

export function CandidateLayout({
  user,
  onSignOut,
  children,
}: {
  user: User;
  onSignOut: () => void;
  children?: React.ReactNode;
}) {
  const t = useDict();
  const name = user.full_name || user.email || "User";

  const signOut = async () => {
    await fetch("/api/auth/sign-out", {
      method: "POST",
      credentials: "include",
    });
    onSignOut();
    window.location.href = "/careers";
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
      isActive
        ? "bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200"
        : "text-muted hover:bg-surface hover:text-foreground",
    );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl min-w-0 flex-col gap-2 px-3 py-3 sm:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/careers" className="flex min-w-0 items-center gap-2">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white">
                <Sparkles className="h-4 w-4" />
              </span>
              <span className="truncate font-bold">{t.common.appName}</span>
            </Link>
            <div className="ms-auto flex shrink-0 items-center gap-1.5 sm:gap-3">
              <ThemeToggle />
              <LanguageSwitcher />
              <span className="hidden text-sm font-medium lg:inline">
                {initials(name)} · {name}
              </span>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={signOut}
                className="px-2 sm:px-3"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">{t.common.signOut}</span>
              </Button>
            </div>
          </div>
          <nav className="-mx-1 flex items-center gap-1 overflow-x-auto px-1 pb-0.5">
            <NavLink to="/careers" className={linkClass} end>
              <Briefcase className="h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">{t.nav.careers}</span>
            </NavLink>
            <NavLink to="/candidate/interviews" className={linkClass}>
              <ClipboardList className="h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">{t.nav.myInterviews}</span>
            </NavLink>
            <NavLink to="/candidate/profile" className={linkClass}>
              <FileUser className="h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">{t.nav.myCv}</span>
            </NavLink>
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 p-4 md:p-8">
        {children ?? <Outlet />}
      </main>
    </div>
  );
}
